import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { EngagementProductListWithUser } from '@epicuro-next/data/engagement/engagement-models';
import { List, Media, MediaType } from '@epicuro-next/models/engagement';
import { go } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../../actions';
import { selectOpenedList } from '../../selectors';

export const EPIC_SHOW_CREATE_MESSAGE = $localize`:ProductList|Epic Show create success message@@epicShowCreateSuccessMessage:Your Epic Show was created`;

@Injectable()
export class EpicShowEffects {
  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
  );

  private siteId$ = this.store.pipe(select(selectSiteId), filter(isNonNullish));

  private openedList$ = this.store.pipe(
    select(selectOpenedList),
    filter((v: List | null): v is List => !!v),
  );

  public createEpicShow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createEpicShow),
      concatLatestFrom(() => [this.openedList$, this.accountId$, this.siteId$]),
      switchMap(([action, list, accountId, siteId]) => {
        return this.productListApi
          .update({
            list,
            accountId,
            siteId,
            media: action.media,
          })
          .pipe(
            map(() => actions.createEpicShowSuccess({ media: action.media })),
            catchError(() => of(actions.createEpicShowFail())),
          );
      }),
    ),
  );

  public createEpicShowSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createEpicShowSuccess),
      map(() =>
        showSnackBarMessage({
          message: EPIC_SHOW_CREATE_MESSAGE,
          messageType: MessageType.SUCCESS,
        }),
      ),
    ),
  );

  public redirectOnProductListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createEpicShowSuccess),
      withLatestFrom(
        this.store.pipe(
          select(selectOpenedList),
          filter((v: List | null): v is List => !!v),
        ),
      ),
      map(([, list]) =>
        go({
          commands: ['browse', 'product-lists', list.id],
        }),
      ),
    ),
  );

  public updateList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createEpicShowSuccess, actions.removeEpicShowSuccess),
      withLatestFrom(
        this.store.pipe(
          select(selectOpenedList),
          filter((v: List | null): v is List => !!v),
        ),
      ),
      concatMap(([{ media }, list]) => {
        const newList: List = {
          ...list,
          mainMedia: media,
          update: {
            ...list?.update,
            dateUtc: new Date().toISOString(),
          },
        };

        return [
          actions.updateProductListSuccess({
            list: newList as EngagementProductListWithUser,
          }),
          actions.setOpenedListMedia({ media }),
        ];
      }),
    ),
  );

  public removeEpicShow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeEpicShow),
      concatLatestFrom(() => [this.openedList$, this.accountId$, this.siteId$]),
      switchMap(([, list, accountId, siteId]) => {
        const media: Media = {
          ...list.mainMedia,
          mediaType: MediaType.Video,
          metadata: null,
        } as Media;

        return this.productListApi
          .update({ list, accountId, siteId, media })
          .pipe(
            map(() => actions.removeEpicShowSuccess({ media })),
            catchError(() => of(actions.removeEpicShowFail())),
          );
      }),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private productListApi: ProductListApiService,
  ) {}
}
